import { useEffect, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { APPS, appsFor, AppIcon, BrandIcon, useTheme } from '@hana/ui';
import { lastAuthError, recordAuthError, signIn } from '../auth';
import { IC, Stroke } from './Icons';
import { AuthShell } from './Shell';
import s from './Login.module.css';

// Màn đăng nhập HANA One — board Mint-Hub (canvas v486): login · redirect · notcompany · offline.
// Một trang đăng nhập cho mọi vùng nội bộ, chỉ tài khoản Office 365 công ty (QĐ 24/Oct 16:26).

export type LoginSearch = { ret?: string; err?: 'notcompany' | 'failed' };
type View = 'login' | 'redirect' | 'notcompany' | 'offline';

export function ThemeButton() {
  const { theme, toggle } = useTheme();
  const k = theme === 'dark' ? 'Giao diện sáng' : 'Giao diện tối';
  return (
    <button type="button" className={s.iconBtn} onClick={toggle} aria-label={k} title={k}>
      <Stroke d={theme === 'dark' ? IC.sun : IC.moon} />
    </button>
  );
}

export function Login() {
  const { ret, err } = useSearch({ from: '/' });
  const retApp = appsFor(true).find((a) => a.key === ret);
  const [view, setView] = useState<View>(err === 'notcompany' ? 'notcompany' : 'login');
  const [failed, setFailed] = useState(err === 'failed');
  const [code, setCode] = useState<string | null>(err === 'failed' ? lastAuthError() : null);

  useEffect(() => {
    const off = () => setView('offline');
    window.addEventListener('offline', off);
    return () => window.removeEventListener('offline', off);
  }, []);

  const go = () => {
    if (!navigator.onLine) return setView('offline');
    setView('redirect');
    setFailed(false);
    signIn(ret).catch((e) => {
      setCode(recordAuthError(e));
      setView('login');
      setFailed(true);
    });
  };

  return (
    <AuthShell top={<ThemeButton />}>
      <main className={s.card} aria-labelledby="hbT">
        {retApp && view !== 'notcompany' && (
          <div className={s.ret} title="Đăng nhập xong sẽ quay lại đúng trang này">
            <AppIcon app={retApp.key} size={28} />
            <span>{retApp.name}</span>
          </div>
        )}

        {view === 'login' && (
          <>
            {failed && (
              <div role="alert" className={s.alert} data-err={code ?? undefined} title={code ?? undefined}>
                <b>Không kết nối được máy chủ đăng nhập</b>
              </div>
            )}
            <h1 id="hbT" className={s.title}>
              {retApp ? 'Đăng nhập để tiếp tục' : 'Đăng nhập HANA One'}
            </h1>
            <p className={s.sub}>{retApp ? `Đăng nhập để tiếp tục vào ${retApp.name}` : 'Một tài khoản Microsoft cho mọi vùng nội bộ'}</p>
            <ul className={s.apps} aria-label="Mở được sau khi đăng nhập">
              {APPS.map((a) => (
                <li key={a.key} className={s.app} title={a.name}>
                  <AppIcon app={a.key} />
                  {a.short}
                </li>
              ))}
            </ul>
            <button type="button" className={s.sso} onClick={go}>
              <BrandIcon name="microsoft" />
              Đăng nhập bằng Microsoft
            </button>
          </>
        )}

        {view === 'redirect' && (
          <>
            <div className={s.hop} aria-hidden="true">
              <AppIcon app="hub" size={48} />
              <span className={s.dash} />
              <span className={s.ms}>
                <BrandIcon name="microsoft" size={24} />
              </span>
            </div>
            <h1 id="hbT" role="status" className={s.title2}>
              Đang chuyển tới Microsoft…
            </h1>
            <p className={s.sub}>login.microsoftonline.com · tài khoản công ty</p>
            <div role="progressbar" aria-label="Đang chuyển" className={s.bar}>
              <i />
            </div>
            <button type="button" className={s.btn2} onClick={() => setView('login')}>
              <Stroke d={IC.cancel} size={16} w={1.8} />
              Huỷ
            </button>
          </>
        )}

        {view === 'notcompany' && (
          <Fail
            d={IC.userX}
            tone="err"
            h="Tài khoản không thuộc công ty"
            sub="Chỉ đăng nhập được bằng tài khoản @hanalogisticsvn.com"
            k="Dùng tài khoản khác"
            onK={go}
            k2="Liên hệ IT"
            href2="mailto:it@hanalogisticsvn.com"
          />
        )}
        {view === 'offline' && (
          <Fail
            d={IC.wifiOff}
            tone="warn"
            h="Mất kết nối mạng"
            sub="Kiểm tra Wi-Fi hoặc 4G rồi thử lại"
            k="Thử lại"
            onK={() => navigator.onLine && setView('login')}
          />
        )}

        <div className={s.foot} title="Tài khoản Office 365 công ty">
          <Stroke d={IC.team} size={14} />
          Office 365 · @hanalogisticsvn.com
        </div>
        <div className={s.help}>
          <span title="Hỗ trợ IT">
            <Stroke d={IC.help} size={14} />
            it@hanalogisticsvn.com
          </span>
        </div>
      </main>
    </AuthShell>
  );
}

function Fail(p: { d: string; tone: 'err' | 'warn'; h: string; sub: string; k: string; onK: () => void; k2?: string; href2?: string }) {
  return (
    <>
      <span className={`${s.badge} ${p.tone === 'err' ? s.badgeErr : s.badgeWarn}`} aria-hidden="true">
        <Stroke d={p.d} size={24} w={2} />
      </span>
      <h1 id="hbT" role="alert" className={s.title2}>
        {p.h}
      </h1>
      <p className={s.sub}>{p.sub}</p>
      <button type="button" className={s.sso} onClick={p.onK}>
        {p.k}
      </button>
      {p.k2 && p.href2 && (
        <a className={s.btn2} href={p.href2}>
          {p.k2}
        </a>
      )}
    </>
  );
}
